/**
 * Overlay de precios de la lista "online" (SCRUM-351) sobre MarketCatalogData.
 * A diferencia de applyB2bChannelToCatalogData (catálogo restringido: oculta
 * productos sin item en el canal), acá el catálogo online NO debe reducirse:
 * un producto sin item en la lista (ej. creado después del provisioning
 * inicial) sigue visible con su propio precio_unitario/precio_por_kg como
 * fallback — la lista solo SOBRESCRIBE precio cuando tiene un item real.
 */
import type { MarketCatalogData, MarketProduct } from "@/lib/market-catalog";
import { roundPriceFields, type PriceRoundingMode } from "@/lib/price-rounding";

export type OnlinePriceListItem = {
  productId: string | null;
  tipoVenta: string;
  b2bPrecioUnitario: number | null;
  b2bPrecioPorKg: number | null;
};

function cloneProduct(p: MarketProduct): MarketProduct {
  return { ...p, variants: p.variants?.map((v) => ({ ...v })), addons: p.addons?.map((a) => ({ ...a })) };
}

export function applyOnlinePriceListToCatalogData(
  data: MarketCatalogData,
  items: OnlinePriceListItem[],
  priceRounding?: PriceRoundingMode | null,
): MarketCatalogData {
  const byProduct = buildPriceListItemMap(items);
  if (byProduct.size === 0 && (!priceRounding || priceRounding === "none")) return data;

  const nextProducts = data.products.map((raw) => {
    const base = byProduct.size > 0
      ? resolvePriceListOverride(byProduct, raw.id, raw.tipoVenta, {
          precioUnitario: raw.precioUnitario,
          precioPorKg: raw.precioPorKg,
        })
      : { precioUnitario: raw.precioUnitario, precioPorKg: raw.precioPorKg };
    const override = roundPriceFields(base, priceRounding);
    if (override.precioUnitario === raw.precioUnitario && override.precioPorKg === raw.precioPorKg) return raw;
    return { ...cloneProduct(raw), ...override };
  });

  return { ...data, products: nextProducts };
}

/** Indexa los items de una lista por producto — compartido entre el overlay del storefront y el del POS. */
export function buildPriceListItemMap(items: OnlinePriceListItem[]): Map<string, OnlinePriceListItem> {
  const byProduct = new Map<string, OnlinePriceListItem>();
  for (const item of items) {
    if (item.productId) byProduct.set(item.productId, item);
  }
  return byProduct;
}

/**
 * Resuelve el precio de un producto puntual contra una lista ya indexada, con fallback
 * al precio recibido si el producto no tiene item en la lista (o el item no tiene precio
 * cargado para su tipo de venta) — mismo contrato "nunca deja sin precio" del storefront,
 * reusado acá para que POS y tienda online resuelvan precio con la misma función.
 */
export function resolvePriceListOverride(
  byProduct: Map<string, OnlinePriceListItem>,
  productId: string,
  tipoVenta: string,
  fallback: { precioUnitario: number | null; precioPorKg: number | null },
): { precioUnitario: number | null; precioPorKg: number | null } {
  const item = byProduct.get(productId);
  if (!item) return fallback;
  const tipo = item.tipoVenta || tipoVenta || "unidad";
  if (tipo === "peso") {
    return { precioUnitario: fallback.precioUnitario, precioPorKg: item.b2bPrecioPorKg ?? fallback.precioPorKg };
  }
  return { precioUnitario: item.b2bPrecioUnitario ?? fallback.precioUnitario, precioPorKg: fallback.precioPorKg };
}

/**
 * Cuál lista de precios usa el POS: la propia si el tenant fijó una específica para
 * mostrador, si no la misma que la tienda online (comportamiento por defecto — un
 * comercio con una sola lista no tiene que pensar en "cuál usa el POS"), si no ninguna
 * (precio directo del producto, comportamiento previo a esta feature).
 */
export function resolveActivePosPriceListId(config: { onlinePriceListId?: string | null; posPriceListId?: string | null } | null | undefined): string | null {
  return config?.posPriceListId ?? config?.onlinePriceListId ?? null;
}
